import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 6px;
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: 0.95rem;
  min-width: 240px;
`;

const Button = styled.button`
  padding: 0.5rem 1.25rem;
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  overflow: hidden;
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  background: ${theme.colors.background.paper};
  &:last-child { border-bottom: none; }
`;

const Status = styled.span`
  flex-shrink: 0;
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

const Label = styled.div`
  flex: 1;
  min-width: 0;
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const Meta = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
`;

const RemoveBtn = styled.button`
  padding: 0.25rem 0.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const POLL_INTERVAL = 2000;

const AIKnowledgeAdmin = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addingUrl, setAddingUrl] = useState(false);
  const [url, setUrl] = useState('');
  const [urlName, setUrlName] = useState('');
  const [needsMigration, setNeedsMigration] = useState(false);
  const [runningMigration, setRunningMigration] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setNeedsMigration(false);
    try {
      const res = await api.get('/api/admin/ai-knowledge');
      setDocs(res.data?.documents || []);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.includes('does not exist') || err.response?.data?.needsMigration) {
        setNeedsMigration(true);
      }
      toast.error(msg || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const runMigration = async () => {
    setRunningMigration(true);
    try {
      await api.post('/api/admin/ai-knowledge/run-migration');
      toast.success('Database migration completed');
      setNeedsMigration(false);
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Migration failed');
    } finally {
      setRunningMigration(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const hasProcessing = docs.some((d) => d.status === 'processing');
  useEffect(() => {
    if (!hasProcessing) return;
    const id = setInterval(fetchDocs, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [hasProcessing, fetchDocs]);

  const handleFileSelect = (e) => {
    const files = e.target?.files;
    setSelectedFiles(files ? Array.from(files) : []);
  };

  const handleUploadClick = async () => {
    if (!selectedFiles.length) {
      toast.warning('Choose files first');
      return;
    }
    setUploading(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const file of selectedFiles) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await api.post('/api/admin/ai-knowledge/upload', fd, { timeout: 60000 });
          ok++;
        } catch (err) {
          fail++;
          toast.error(`"${file.name}": ${err.response?.data?.error || err.message}`);
        }
      }
      if (ok) {
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchDocs();
        if (fail) toast.success(`${ok} uploaded` + (fail ? `, ${fail} failed` : ''));
        else if (ok === 1) toast.success('File uploaded');
        else toast.success(`${ok} files uploaded`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAddUrl = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u) {
      toast.warning('Enter a URL first');
      return;
    }
    setAddingUrl(true);
    try {
      await api.post('/api/admin/ai-knowledge/url', {
        url: u,
        name: urlName.trim() || undefined,
      }, { timeout: 30000 });
      toast.success(`"${u}" added. Indexing in progress.`);
      setUrl('');
      setUrlName('');
      fetchDocs();
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error || (data?.errors?.[0]?.msg) || err.message || 'Add URL failed';
      toast.error(msg);
    } finally {
      setAddingUrl(false);
    }
  };

  const handleRemove = (id) => {
    toast(
      (t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>Remove from knowledge base?</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                performRemove(id);
              }}
              style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
            >
              Remove
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{ padding: '6px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 10000 }
    );
  };

  const performRemove = async (id) => {
    try {
      await api.delete(`/api/admin/ai-knowledge/${id}`);
      toast.success('Removed');
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const statusIcon = (d) => {
    if (d.status === 'indexed') return '✓';
    if (d.status === 'failed') return '✗';
    return '○';
  };

  const statusColor = (d) => {
    if (d.status === 'indexed') return theme.colors.success.main;
    if (d.status === 'failed') return theme.colors.error.main;
    return theme.colors.text.secondary;
  };

  return (
    <Section>
      <div>
        <strong>Files</strong>
        <p style={{ fontSize: '0.9rem', color: theme.colors.text.secondary, margin: '0.25rem 0 0.5rem' }}>
          1. Choose files (multiple) → 2. Click Upload
        </p>
        <Row>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.pptx"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            style={{ maxWidth: 280 }}
          />
          <Button
            onClick={handleUploadClick}
            disabled={uploading || !selectedFiles.length}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          {selectedFiles.length > 0 && (
            <span style={{ color: theme.colors.text.secondary }}>
              {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
            </span>
          )}
        </Row>
      </div>

      <div>
        <strong>URLs</strong>
        <p style={{ fontSize: '0.9rem', color: theme.colors.text.secondary, margin: '0.25rem 0 0.5rem' }}>
          Enter URL and name, then click Add URL
        </p>
        <form onSubmit={handleAddUrl}>
          <Row>
            <Input
              type="text"
              placeholder="https://example.com/manual"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={addingUrl}
            />
            <Input
              type="text"
              placeholder="Display name"
              value={urlName}
              onChange={(e) => setUrlName(e.target.value)}
              disabled={addingUrl}
              style={{ minWidth: 140 }}
            />
            <Button type="submit" disabled={addingUrl}>
              {addingUrl ? 'Adding…' : 'Add URL'}
            </Button>
          </Row>
        </form>
      </div>

      <div>
        <strong>Stored items ({docs.length})</strong>
        {needsMigration && (
          <div style={{
            margin: '0.5rem 0',
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: 8,
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600 }}>Database tables missing. Click to create them:</p>
            <Button onClick={runMigration} disabled={runningMigration}>
              {runningMigration ? 'Running…' : 'Run database migration'}
            </Button>
          </div>
        )}
        <p style={{ fontSize: '0.9rem', color: theme.colors.text.secondary, margin: '0.25rem 0 0.5rem' }}>
          ✓ indexed • ○ indexing • ✗ failed
        </p>
        {loading ? (
          <p style={{ color: theme.colors.text.secondary }}>Loading…</p>
        ) : docs.length === 0 ? (
          <p style={{ color: theme.colors.text.secondary }}>No files or URLs yet.</p>
        ) : (
          <List>
            {docs.map((d) => (
              <ListItem key={d.id}>
                <Status style={{ color: statusColor(d) }} title={d.status}>
                  {statusIcon(d)}
                </Status>
                <Label>
                  {d.source_name}
                  {d.source_url && (
                    <Meta style={{ display: 'block', marginTop: 2 }}>
                      {d.source_url}
                    </Meta>
                  )}
                </Label>
                <Meta>{d.chunk_count ?? 0} chunks</Meta>
                <RemoveBtn onClick={() => handleRemove(d.id)}>Remove</RemoveBtn>
              </ListItem>
            ))}
          </List>
        )}
      </div>
    </Section>
  );
};

export default AIKnowledgeAdmin;
